<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3">
    <v-dialog v-model="showMembersDialog" width="800px">
      <v-card class="search-modal">
        <h3 class="mb-1">Search & Add People</h3>
        <p class="mb-1">A minimum of 10 distinct members/officers/directors are needed. Current number of members added: <b><span :class="uniqueSelectedMembers.length >= 10 ? 'success-text' : 'error-text'">{{uniqueSelectedMembers.length}}</span></b></p>
        <v-form class="search-form" @submit.prevent="search">
          <v-text-field
            outlined
            prepend-inner-icon="mdi-magnify"
            v-model="membershipID"
            label="Search Member ID"
          />
        </v-form>
        <p v-if="!searchResults.length && hasSearched">No users found</p>
        <v-data-table
          v-if="searchResults.length"
          :headers="headers"
          :items="searchResults"
          hide-default-footer
          hide-default-header>
          <template v-slot:item="row">
            <v-tooltip dark right>
                <template v-slot:activator="{ on, attrs }">
                  <tr v-bind="attrs" v-on="on">
                    <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
                    <td>{{ row.item.memberId }}</td>
                    <td>
                      <font-awesome-icon icon="id-card" :color="row.item.canBeCoowner == false ? 'red' : isValidOfficer(row.item) ? 'green' : 'gold'" />
                    </td>
                    <td>
                      <a :class="{'disabled-link': isValidCoOwner(row.item) == false,}" v-on:click="addSelectedMember(row.item)">
                        <font-awesome-icon icon="plus" :color="isValidCoOwner(row.item) ? 'blue' : 'gray'" />
                      </a>
                    </td>
                  </tr>
                </template>
                <span v-if="isValidCoOwner(row.item) == false">This is not a valid affiliate club member/officer.</span>
                <span v-else-if="isValidOfficer(row.item) == false">This is not a valid affiliate club officer.</span>
                <span v-else>Valid Member</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <v-btn
          color="primary"
          class="white--text"
          block
          v-on:click="submit()">Done</v-btn>
        <div v-if="selectedMembers.length">
          <h2 class="mt-3">Added Affiliate Members</h2>
          <v-data-table
            :headers="headers"
            :items="selectedMembers"
            :items-per-page="20"
            hide-default-footer
            hide-default-header>
            <template v-slot:item="row">
              <tr>
                <td>
                  <span v-if="row.item.position.length==0">---</span>
                  <span v-else> {{row.item.position}}</span>
                </td> 
                <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
                <td>{{ row.item.memberId }}</td>
                <td>
                  <a v-on:click="removeSelectedMember(row.item)">
                    <font-awesome-icon icon="minus" />
                  </a>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showValidationDialog">
      <v-card class="search-modal">
        <h2>Validation Results</h2>
        <v-form class="search-form">
          <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">
            <pre>{{ validationResult.errors.join("\r\n") }}</pre>
          </v-alert>
          <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined>
            <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
          </v-alert>
          <v-btn
            large
            color="primary"
            width="125px"
            :disabled="!validationResult.canBeSaved"
            v-on:click="saveChanges">
            <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
            Save
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
    <div class="profile-content">
      <div v-if="affiliateId && !affiliateInfo.affiliateContact && isLoaded">
        <v-alert width="100%" class="pr-2 pl-2"type="error"> This Affiliate {{affiliateId}} is missing required data.</v-alert>
      </div>
      <div v-else>
        <v-row class="ma-0 mb-3">
          <h1 v-if="affiliateId">
            Affiliate ID #{{ affiliateId }} {{ affiliateInfo.affiliateName }}
            <v-chip
              v-if="affiliateInfo.isExpired"
              text-color="white"
              class="ma-1"
              color="red">
              <v-avatar left>
                <v-icon>mdi-close-circle-outline</v-icon>
              </v-avatar>
              Expired
            </v-chip>
            <v-chip
              v-else
              text-color="white"
              class="ma-1"
              color="green">
              <v-avatar left>
                <v-icon>mdi-checkbox-marked-circle</v-icon>
              </v-avatar>
              Active
            </v-chip>
          </h1>
          <h1 class="mb-3" v-else>Create New Affiliate</h1>
          <v-spacer />
        </v-row>
        <div>
          <v-row>
            <v-textarea
              :disabled="!isEditMode"
              :visible="!isEditMode"
              outlined
              auto-grow
              clearable
              rows="1"
              v-model="affiliateInfo.adminNotes"
              label="Description of changes (optional)" />
            <v-btn
              class="ml-1 mr-1"
              color="primary"
              @click="toggleEditMode">
              {{ isEditMode ? "Save" : "Edit" }}
            </v-btn>
          </v-row>
          <v-row>
            <v-col>
              <h3 v-if="affiliateId && affiliateInfo.affiliateContact != null"> Affiliate Club Information (Member ID: {{affiliateInfo.affiliateContact.id}})</h3>
              <h3 v-else>Enter Affiliate Club Details</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-row>
                <v-text-field
                  v-model="affiliateInfo.affiliateName"
                  :disabled="!isEditMode"
                  label="Name*" class="mr-1"
                  :rules="[v => (v || '' ).length <= 50 || 'Affiliate Name is limited to 50 chars']"
                  outlined
                />
                <DatepickerFormatted
                  :disabled="!isEditMode"
                  :date="affiliateInfo.membershipExpiryDate"
                  label="Member Expiry Date*"
                  v-on:updateDates="updateDates($event, 'membershipExpiryDate')" />
              </v-row>
              <v-row v-if="affiliateInfo.affiliateContact != null">
                <v-text-field
                  v-model="affiliateInfo.affiliateContact.email"
                  :disabled="!isEditMode"
                  clearable
                  label="Email"
                  class="mr-1"
                  outlined
                  :rules="[validateEmail]"
                />
                <InternationalPhoneNumberInput
                  classNames="mr-1"
                  :phone="affiliateInfo.affiliateContact.phone"
                  :disabled="!isEditMode"
                  @changeData="(v) => affiliateInfo.affiliateContact.phone = v.number.e164"
                />
              </v-row>
              <v-row>
                <v-text-field
                  v-model="affiliateInfo.termOfOffice"
                  :disabled="!isEditMode"
                  class="mr-1"
                  :rules="[isValidNumber]"
                  label="Term of Office"
                  clearable
                  outlined
                />
                <v-text-field
                  v-model="affiliateInfo.region"
                  :disabled="!isEditMode"
                  clearable
                  label="Region"
                  class="mr-1"
                  :rules="[isValidNumber]"
                  outlined
                />
                <v-combobox v-model="affiliateInfo.homeState"
                        :items="affiliateHomeStates"
                        label="Home State"
                        :disabled="!isEditMode"
                        clearable>
                </v-combobox>
              </v-row>
              <v-row>
                <v-text-field
                  v-model="affiliateInfo.website"
                  :disabled="!isEditMode"
                  label="Website"
                  :rules="[validateWWWUrl]"
                  outlined
                />
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3 v-if="affiliateId">Conformation Point Schedule</h3>
              <h3 v-else>Enter Affiliate Conformation Point Schedule Details</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="affiliateInfo.dogCount"
                :disabled="!isEditMode"
                clearable
                label="Dog Count"
                class="mr-1"
                :rules="[isValidNumber]"
                outlined
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="affiliateInfo.altDogCount"
                :disabled="!isEditMode"
                clearable
                :rules="[isValidNumber]"
                label="Alt Dog Count"
                class="mr-1"
                outlined
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="affiliateInfo.bitchCount"
                :disabled="!isEditMode"
                clearable
                :rules="[isValidNumber]"
                label="Bitch Count"
                class="mr-1"
                outlined
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="affiliateInfo.altBitchCount"
                :disabled="!isEditMode"
                clearable
                :rules="[isValidNumber]"
                label="Alt Bitch Count"
                class="mr-1"
                outlined
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select
                v-model="affiliateInfo.dogSched"
                :items="pointScheduleIds"
                outlined
                label="Dog Sched"
                clearable
                class="mr-1"
                :disabled="!isEditMode"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="affiliateInfo.altDogSched"
                :items="pointScheduleIds"
                outlined
                label="Alt Dog Sched"
                clearable
                class="mr-1"
                :disabled="!isEditMode"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="affiliateInfo.bitchSched"
                :items="pointScheduleIds"
                label="Bitch Sched"
                clearable
                class="mr-1"
                :disabled="!isEditMode"
                outlined
              />
            </v-col>
            <v-col>
              <v-select
                v-model="affiliateInfo.altBitchSched"
                :items="pointScheduleIds"
                outlined
                label="Alt Bitch Sched"
                clearable
                class="mr-1"
                :disabled="!isEditMode"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3>Mailing Address</h3>
            </v-col>
          </v-row>
          <v-row v-if="affiliateInfo.affiliateContact != null">
            <v-col>
              <v-row>
                <CountryDropdown
                  classNames="mr-1"
                  :disabled="!isEditMode"
                  :selectedCountry="affiliateInfo.affiliateContact.country"
                  v-on:update="updateCountry"
                />                
                <StateDropdown
                  :required="affiliateInfo.affiliateContact.country === country_USA"
                  :disabled="!isEditMode || affiliateInfo.affiliateContact.country !== country_USA"
                  :selectedState="affiliateInfo.affiliateContact.stateAbbreviation"
                  v-on:update="affiliateInfo.affiliateContact.stateAbbreviation = $event"
                />
              </v-row>
              <v-row>
                <v-text-field
                  v-model="affiliateInfo.affiliateContact.street"
                  :disabled="!isEditMode"
                  class="mr-1"
                  label="Address"
                  :rules="[v => (v || '' ).length <= 50 || 'Address is limited to 50 chars']"
                  outlined
                />
                <v-text-field
                  v-model="affiliateInfo.affiliateContact.city"
                  :disabled="!isEditMode"
                  label="City"
                  :rules="[v => (v || '' ).length <= 50 || 'City is limited to 50 chars']"
                  outlined
                />
              </v-row>
              <v-row>
                <v-text-field
                  class="mr-1"
                  v-model="affiliateInfo.affiliateContact.international"
                  :disabled="!isEditMode || !affiliateInfo.affiliateContact.country || affiliateInfo.affiliateContact.country === country_USA"
                  label="International Area"
                  :rules="[v => (v || '' ).length <= 50 || 'International Area is limited to 50 chars']"
                  outlined
                />
                <v-text-field
                  v-model="affiliateInfo.affiliateContact.zip"
                  :disabled="!isEditMode"
                  label="Zip"
                  :rules="[v => (v || '' ).length <= 10 || 'Zip is limited to 10 chars']"
                  outlined
                />
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3>
                Affiliate Members
                <v-chip v-if="affiliateInfo.members" text-color="white" class="ma-1" color="blue">{{getUniqueMembersCount(affiliateInfo.members)}}</v-chip>
              </h3>
              <div class="my-2" v-if="isEditMode">
                <a v-on:click="toggleMembersDialog()">
                  <font-awesome-icon icon="plus" />
                  Add/Edit affiliate members/officers/directors
                </a>
              </div>
            </v-col>
          </v-row>
          <v-row class="pt-0 mt-0">
            <v-col>
              <div>
                <v-data-table
                  :headers="headers"
                  :items="affiliateInfo.members"
                  :items-per-page="100"
                  item-key="rowKey"
                  :hide-default-footer="true"
                  class="elevation-1">
                  <template v-slot:item.name="{ item }">
                    <router-link :to="`/admin/people/${item.personId}`" target="_blank">{{ item.name }}</router-link>
                  </template>
                  <template v-slot:item.position="{ item }">
                    <v-select
                      v-model="item.position"
                      :items="positionOptions"
                      label="Position"
                      :disabled="!isEditMode"
                      solo
                    />
                    <v-checkbox
                      v-if="item.position=='Show Coordinator'"
                      v-model="item.isPrimary"
                      :disabled="!isEditMode"
                      label="Is Primary"
                    />
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </div>
        <v-divider class="my-3"></v-divider>
        <v-row class="pt-0 mt-0">
          <v-col>
            <h3>Notes</h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              disabled
              @input="updateText('notes', $event)"
              :value="affiliateInfo.notes"
              outlined
              rows="1"
              auto-grow
            />
          </v-col>
        </v-row>
      </div>
    </div>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}
      <template v-slot:action="{ attrs }">
      <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
        Close
      </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
      <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
        Close
      </v-btn>
      </template>
    </v-snackbar>  
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";
import { uniqBy } from "lodash";

import AddPersonModal from "@/components/common/AddPersonModal.vue";
import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import DatepickerFormatted from "@/components/common/inputs/DatepickerFormatted.vue";
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import showService from "@/services/admin/showService";
import affiliateService from "@/services/admin/affiliateService";
import registerLeaseService from "@/services/portal/registerLeaseService";
import registerAffiliateService from "@/services/portal/registerAffiliateService";

import validateEmail from '@/helpers/validate-email';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import { Countries, Messages } from "@/constants";

import { LesseeResult } from "@/types/portal/Lease";
import { PointSchedule } from "@/types/admin/Show";

// TODO: Once time allows debounce the email validation here
export default Vue.extend({
  name: "AffiliateDetails",
  components: {
      AddPersonModal,
      DatepickerFormatted,
      CountryDropdown,
      StateDropdown,
      InternationalPhoneNumberInput
  },
  props: {
    affiliateId: { type: String },
  },
  data: function () {
      return {
          isAddressValid: true,
          affiliateInfo: {
              affiliateContact: {
                  country: Countries.USA,
                  stateAbbreviation: "",
                  international: "",
              },
              affiliateRep: {},
              members: [],
          } as any,
          isEditMode: false,
          showValidationDialog: false,
          dialog: false,
          dialogDelete: false,
          validationResult: {} as any,
          showMembersDialog: false,
          affiliatePeopleDialog: false,
          affiliateMemberTypes: {},
          affiliateHomeStates: [] as any[],
          hasSearched: false,
          searchResults: [] as any[],
          membershipID: "",
          affiliatePeople: [] as any,
          selectedMembers: [] as any[],
          uniqueSelectedMembers: [] as any[],
          pointSchedules: [] as PointSchedule[],
          pointScheduleIds: [] as any[],
          positionOptions: [ "", "President", "Affiliate Rep", "Vice President", "Second VP", "Director", "Secretary", "Treasurer", "Show Coordinator", "Member" ],
          headers: [
              { text: 'Position', align: 'start', sortable: true, value: 'position', width: '30%' },
              { text: 'Name', value: 'name', align:'center' },
              { text: 'Member Id', value: 'memberId' },
          ],
          isLoaded: false,
          errorMessage: '',
          infoMessage: '',
          showErrorMessage: false,
          showInfoMessage: false,
          country_USA: Countries.USA
      };
  },
  created: function () {
      this.reloadData();
      registerAffiliateService.getAffiliateMemberTypes().then((res: any) => {
          this.affiliateMemberTypes = res.memberTypes;
      });
      affiliateService.getAffiliateHomeStates().then((res: any) => {
          this.affiliateHomeStates = res.states;
      });
      showService.getPointSchedules().then((res: any) => {
          this.pointSchedules = res;
          this.pointScheduleIds.push("");
          for (let i = 0; i < this.pointSchedules.length; i++) {
              this.pointScheduleIds.push(this.pointSchedules[i].pointScheduleId);
          }
      });
  },
  watch: {
      affiliateId: function (newVal: String) {
          affiliateService
              .getAffiliateById(this.$props.affiliateId)
              .then((res) => {
                  this.setAffiliateInfo(res);
              });
      },
      showMembersDialog: function (newVal: boolean) {
          //this is handling situation when the user closes dialog
          //by clicking outside of it
          if (!newVal) {
              this.membershipID = "";
              this.hasSearched = false;
              this.searchResults = [] as LesseeResult[];
              
          }
      },
      membershipID: {
          handler: debounce(
              1000,
              function (this: any, newVal: string, oldVal: string) {
                  if (newVal === oldVal || !newVal) return;
                  this.search();
              }
          ),
          deep: true,
      },
      selectedMembers: function (){
        this.uniqueSelectedMembers = uniqBy(this.selectedMembers, mem => (mem as any).personId);
      }
  },
  methods: {
      reloadData: function () {
          this.isLoaded = false;
          if (this.affiliateId) {
              affiliateService
                  .getAffiliateById(this.affiliateId)
                  .then((res) => {
                      this.setAffiliateInfo(res);
                      this.isEditMode = false;
                      this.fillSelectedMembers();
                      this.isLoaded = true;
                  });
          }
          else {
              this.affiliateInfo = {
                  affiliateContact: {
                      country: Countries.USA,
                      stateAbbreviation: "",
                      international: "",
                  },
                  affiliateRep: {},
                  members: [],
              } as any;
              this.isEditMode = true;
              this.selectedMembers = [] as any[];
          }

          this.showValidationDialog = false;
          this.errorMessage = '';
          this.showErrorMessage = false;
          this.validationResult = {};
      },
      validateAndConfirmSavingChanges: function () {
          this.normalizeAddressRegion();
          if (this.isEverythingValid()) {
              affiliateService
                  .validateAffiliateEdit(this.affiliateInfo)
                  .then((res: any) => {
                      this.validationResult = res;
                      if (this.validationResult.errors.length > 0 || this.validationResult.warnings.length > 0) {
                          this.showValidationDialog = true;
                      }
                      else {
                          this.saveChanges();
                          this.validationResult = {};
                      }
                  })
          }
          else {
              this.isEditMode = false; //will toggle it back to edit mode
          }
          
      },      
      toggleEditMode: function () {
          if (this.isEditMode) {
              this.validateAndConfirmSavingChanges();
          }

          this.isEditMode = !this.isEditMode;
      },
      saveChanges: function () {
          this.normalizeAddressRegion();
          this.errorMessage = "";
          this.showErrorMessage = false;
          this.showValidationDialog = false;
          this.$store.dispatch("changeLoaderStatus", true);

          affiliateService
              .editAffiliateById(this.affiliateInfo)
              .then((res) => {
                  if (res.success) {
                      if (res.id && res.id != this.$props.affiliateId) {
                          this.$router.push({ path: `/admin/affiliates/${res.id}` }).catch(this.$handleNavigationError);
                      }
                      this.reloadData();
                      this.isEditMode = false;
                      this.infoMessage = Messages.SaveSuccessful;
                      this.showInfoMessage = true;
                  } else {              
                      this.errorMessage = Messages.SaveError + " " + res.messages.join(', ');
                      this.showErrorMessage = true;
                  }
              })
              .catch((e) => {                  
                this.errorMessage = Messages.SaveError;
                  this.showErrorMessage = true;
              })
              .finally(() => this.$store.dispatch("changeLoaderStatus", false));


      },
      updateDates: function (val: any, key: string) {
          this.$data.affiliateInfo[key] = val?.date;
          this.$data.affiliateInfo = Object.assign({}, this.$data.affiliateInfo);
      },
      updateText: function (key: string, event: string) {
          this.$data.affiliateInfo[key] = event;
      },
      updateCountry: function (country: string) {
          this.affiliateInfo.affiliateContact.country = country;
          this.normalizeAddressRegion();
      },
      setAffiliateInfo: function (affiliateInfo: any) {
          this.affiliateInfo = affiliateInfo;
          if (this.affiliateInfo.members) {
              this.affiliateInfo.members.forEach((member: any, index: number) => {
                  this.$set(member, "rowKey", `${member.personId}-${member.positionId}-${index}`);
              });
          }
          this.normalizeAddressRegion();
      },
      normalizeAddressRegion: function () {
          const contact = this.affiliateInfo.affiliateContact;
          if (!contact) return;

          const region = normalizeAddressRegion(
              contact.country,
              contact.stateAbbreviation,
              contact.international
          );
          contact.stateAbbreviation = region.state;
          contact.international = region.international;
      },
      toggleMembersDialog: function () {
          this.showMembersDialog = !this.showMembersDialog;
          if (this.showMembersDialog) {
              this.fillSelectedMembers();
          }
      },      
      fillSelectedMembers() {
          this.selectedMembers = [] as any;
          if (this.affiliateInfo.members) {
              for (let i = 0; i < (this as any).affiliateInfo.members.length; i++) {
                  this.selectedMembers.push({
                      position: (this as any).affiliateInfo.members[i].position,
                      isPrimary: (this as any).affiliateInfo.members[i].isPrimary,
                      memberId: (this as any).affiliateInfo.members[i].memberId,
                      personId: (this as any).affiliateInfo.members[i].personId,
                      firstName: (this as any).affiliateInfo.members[i].firstName,
                      lastName: (this as any).affiliateInfo.members[i].lastName,
                      name: (this as any).affiliateInfo.members[i].name,
                      positionId: (this as any).affiliateInfo.members[i].positionId,
                      rowKey: (this as any).affiliateInfo.members[i].rowKey,
                  });
              }
          }  
      },
      addSelectedMember(row: LesseeResult) {
          if (!row.canBeCoowner) return;
          this.searchResults = [] as LesseeResult[];
          this.hasSearched = false;
          
          let item = {
              firstName: row.firstName,
              lastName: row.lastName,
              memberId: row.memberId,
              personId: row.personId,
              position: row.memberType === 5 ? "Member" : "",
              memberType: row.memberType,
              name: row.firstName + ' ' + row.lastName,
              isPrimary: false,
          };
          this.selectedMembers.push(item);
      },
      removeSelectedMember(row: LesseeResult) {
          this.selectedMembers.splice(this.selectedMembers.indexOf(row), 1); 
      },
      saveMemberChanges() {
          if (this.affiliateInfo.members) {
              while (this.affiliateInfo.members.length > 0) {
                  this.affiliateInfo.members.pop();
              }
          }
          this.affiliateInfo.members = [] as any[];

          if (this.selectedMembers.length) {
              for (let i = 0; i < (this as any).selectedMembers.length; i++) {
                  this.affiliateInfo.members.push({
                      position: (this as any).selectedMembers[i].position,
                      isPrimary: (this as any).selectedMembers[i].isPrimary,
                      memberId: (this as any).selectedMembers[i].memberId,
                      personId: (this as any).selectedMembers[i].personId,
                      firstName: (this as any).selectedMembers[i].firstName,
                      lastName: (this as any).selectedMembers[i].lastName,
                      name: (this as any).selectedMembers[i].name,
                      positionId: (this as any).selectedMembers[i].positionId,
                      rowKey: (this as any).selectedMembers[i].rowKey || `${(this as any).selectedMembers[i].personId}-${i}`,
                  });


              }
          }

          this.showMembersDialog = false;

      },
      isValidNumber(value: string) {
          if (!value) {
              return true;
          }
          if (!isNaN(parseFloat(value))) return true;

          return 'Must be valid number';
          
      },
      isValidCoOwner(coowner: LesseeResult) {
          return coowner.canBeCoowner;
      },
      isValidOfficer(coowner: LesseeResult) {
          return coowner.memberType === 1 || coowner.memberType === 2 || coowner.memberType === 6 || coowner.memberType === 4
      },
      search: function () {
          this.hasSearched = false;
          registerLeaseService
              .searchLessee(this.membershipID)
              .then((response: LesseeResult[]) => {
                  this.hasSearched = true;
                  this.searchResults = response;
              });
      },
      submit() {
          this.saveMemberChanges();
          this.showMembersDialog = false;
      },
      warn(message: string) {
          this.showErrorMessage = true;
          this.errorMessage = message
      },
      isEverythingValid() {
          this.errorMessage = "";
          this.showErrorMessage = false;
          
          if (!this.affiliateInfo.members) {
              this.warn("Affiliate President and Rep are required.");
              return false;
          }

          var emptyList = this.affiliateInfo.members.find((x: any) => x.position == "");
          var presidentList = this.affiliateInfo.members.filter((x: any) => x.position == "President");

          if (emptyList) {
              this.warn("All members must have an assigned position");
              return false;
          }

          if (!presidentList.length) {
              this.warn("Must include a president");
              return false;
          } else if (presidentList.length > 1)
          {
              this.warn("Can't have more than one president");
              return false;
          }

          if (this.affiliateInfo.membershipExpiryDate == null) {
              this.warn("Affiliate Club expiration date is required.");
              return false;
          }
          for (let i = 0; i < (this as any).affiliateInfo.members.length; i++) {
              let position = (this as any).affiliateInfo.members[i].position;
              let filteredPosition = this.$data.affiliateMemberTypes.filter(
                  (obj: { memberTypeDescription: any; }) => {
                      return obj.memberTypeDescription === position;
                  }
              );
              this.affiliateInfo.members[i].positionId = filteredPosition[0].memberType;
          }

          if (this.affiliateInfo.homeState && this.affiliateInfo.homeState.length > 2) {
              this.warn("Home State is too long.");
              return false;
          }

          if (this.affiliateInfo.affiliateName && this.affiliateInfo.affiliateName.length > 50) {
              this.warn("Affiliate Name is too long.");
              return false;
          } else if (!this.affiliateInfo.affiliateName) {
              this.warn("Affiliate Name is required.");
              return false;
          }
        
          if (this.affiliateInfo.email && this.affiliateInfo.email.length > 255) {
              this.warn("Email is too long.");
              return false;
          }
        
          if (this.affiliateInfo.termOfOffice && isNaN(parseFloat(this.affiliateInfo.termOfOffice))) {
              this.warn("Invalid Term Of Office, must be a number.");
              return false;
          }

          if (this.affiliateInfo.region && isNaN(parseFloat(this.affiliateInfo.region))) {
              this.warn("Invalid Region, must be a number.");  
              return false;
          }

          if (this.affiliateInfo.termOfOffice && isNaN(parseFloat(this.affiliateInfo.termOfOffice))) {
              this.warn("Invalid Term Of Office, must be a number.");
              return false;
          }

          if (this.affiliateInfo.bitchCount && isNaN(parseFloat(this.affiliateInfo.dogCount))) {
              this.warn("Invalid Count, must be a number.");
              return false;
          }

          if (this.affiliateInfo.dogCount && isNaN(parseFloat(this.affiliateInfo.bitchCount))) {
              this.warn("Invalid Count, must be a number."); 
              return false;
          }

          if (this.affiliateInfo.altDogCount && isNaN(parseFloat(this.affiliateInfo.altDogCount))) {
              this.warn("Invalid Count, must be a number.");  
              return false;
          }

          if (this.affiliateInfo.altBitchCount && isNaN(parseFloat(this.affiliateInfo.altBitchCount))) {
              this.warn("Invalid Count, must be a number.");
              return false;
          }
          
          if (this.affiliateInfo.affiliateContact.country !== Countries.USA && (!this.affiliateInfo.affiliateContact.international || !this.affiliateInfo.affiliateContact.street || !this.affiliateInfo.affiliateContact.country)) {
              this.warn("Must provide a valid international mailing address: international, street, country");
              return false;
          } else if (this.affiliateInfo.affiliateContact.country === Countries.USA && (
              !this.affiliateInfo.affiliateContact.city
              || !this.affiliateInfo.affiliateContact.stateAbbreviation
              || !this.affiliateInfo.affiliateContact.zip
              || !this.affiliateInfo.affiliateContact.country
          )) {
              this.warn("Must provide a valid mailing address: city, state/province, zip, country");
              return false;
          }
        
          return true;
      },
      validateEmail(v: string) {
          return (
              validateEmail(v) ||
              'E-mail must be valid'
          );
      },
      validateWWWUrl(v?: string | null) {
          if (!v) {
              return true;
          }

          if (!(/^[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(v))) {
              return 'Url must be valid';
          } else if ((v).length >= 255) {
              return 'Affiliate Website is limited to 255 chars';
          }

          return true;
      },
      getUniqueMembersCount(members: any[]) {
        return (uniqBy(members, mem => (mem as any).personId)).length;
      }
  },
});
</script>
